/**
 * IndexedDB-backed draft persistence for onsite messages.
 * Requirement (Phase-1, req 4): a message draft must survive auth
 * refresh, network drop, PWA restart, and page redirect while the
 * talent is on an active shift. Falls back to localStorage when
 * IndexedDB is unavailable.
 */
const DB_NAME = 'ievntur-live-event-messaging';
const DB_VERSION = 1;
const STORE_DRAFTS = 'onsite_drafts';
const STORE_QUEUE = 'onsite_send_queue';
const FALLBACK_PREFIX = 'ievntur-onsite-';

function _openDb() {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) return reject(new Error('IDB unavailable'));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) db.createObjectStore(STORE_DRAFTS, { keyPath: 'thread_id' });
      if (!db.objectStoreNames.contains(STORE_QUEUE))  db.createObjectStore(STORE_QUEUE,  { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _tx(store, mode = 'readonly') {
  const db = await _openDb();
  return db.transaction(store, mode).objectStore(store);
}

export async function saveDraft(threadId, content) {
  if (!threadId) return;
  try {
    const s = await _tx(STORE_DRAFTS, 'readwrite');
    s.put({ thread_id: threadId, content, updated_at: Date.now() });
  } catch {
    try { localStorage.setItem(FALLBACK_PREFIX + threadId, content); } catch { /* noop */ }
  }
}

export async function loadDraft(threadId) {
  if (!threadId) return '';
  try {
    const s = await _tx(STORE_DRAFTS);
    return await new Promise((res) => {
      const r = s.get(threadId);
      r.onsuccess = () => res(r.result?.content || '');
      r.onerror = () => res('');
    });
  } catch {
    try { return localStorage.getItem(FALLBACK_PREFIX + threadId) || ''; }
    catch { return ''; }
  }
}

export async function clearDraft(threadId) {
  if (!threadId) return;
  try {
    const s = await _tx(STORE_DRAFTS, 'readwrite');
    s.delete(threadId);
  } catch { /* noop */ }
  try { localStorage.removeItem(FALLBACK_PREFIX + threadId); } catch { /* noop */ }
}

/**
 * Persist a send attempt when the network/backend refuses. The queue
 * survives PWA restart. `retrySend` reads the queue and replays each.
 */
export async function enqueueSend(payload) {
  const id = (window.crypto?.randomUUID?.() ||
    `oq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const row = { id, ...payload, queued_at: Date.now() };
  try {
    const s = await _tx(STORE_QUEUE, 'readwrite');
    s.put(row);
  } catch { /* noop */ }
  return id;
}

export async function drainQueue() {
  try {
    const s = await _tx(STORE_QUEUE);
    return await new Promise((res) => {
      const r = s.getAll();
      r.onsuccess = () => res(r.result || []);
      r.onerror = () => res([]);
    });
  } catch { return []; }
}

export async function removeFromQueue(id) {
  try {
    const s = await _tx(STORE_QUEUE, 'readwrite');
    s.delete(id);
  } catch { /* noop */ }
}
